import React, { useEffect, useState, useRef } from "react";
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
} from "@mui/material";
import { toast } from "react-toastify";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PreviewIcon from "@mui/icons-material/Preview";
import InventoryIcon from "@mui/icons-material/Inventory";
import CalculateIcon from "@mui/icons-material/Calculate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import NotesIcon from "@mui/icons-material/Notes";
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from "react-i18next";
import html2pdf from 'html2pdf.js';

// Services
import * as deliveryNoteService from "../services/deliveryNoteService";
import { generateDeliveryNotePdfHtml } from "../services/deliveryNotePdfService";
import { searchProducts } from "../../product/services/productService";
import { searchCustomers } from "../../customers/services/customerService";

// Redux actions
import {
  setSelectedCustomer,
  setSelectedProduct,
  setQuantity,
  setPrice,
  setCustomText,
} from "../../../app/store/slices/deliveryNoteSlice";

// Components
import CustomerSelect from "../../invoices/components/CustomerSelect";
import ProductSelect from "../../invoices/components/ProductSelect";
import InvoicePreviewDialog from "../../../components/InvoicePreviewDialog";
import AddCustomerDialog from "../../customers/components/AddCustomerDialog";
import AddProductDialog from "../../product/components/AddProductDialog";
import DocumentHeaderSection from "../../../components/shared/DocumentHeaderSection";
import DocumentDetailsBar from "../../../components/shared/DocumentDetailsBar";


import * as vatService from '../../companysettings/services/vatService';
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';
import { DocumentType, getDefaultTemplateId, getTemplateContent } from '../../../utils/documentNoteTemplateUtils';

// Hooks
import useStoreInfo from '../../../hooks/useCompanyInfo';

const MAX_CUSTOM_TEXT_LENGTH = 1000;

// Generate delivery note number as DDMMYY + 4 random digits
function generateDeliveryNoteNumber() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `DN-${dd}${mm}${yy}${rand}`;
}

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function AddDeliveryNotePage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: deliveryNoteId } = useParams();
  const isEditMode = Boolean(deliveryNoteId);
  const location = useLocation();
  
  const {
    selectedCustomer,
    selectedProduct,
    quantity,
    price,
    customText,
  } = useSelector((state) => state.deliveryNote);
  
  // Get store info from Redux store
  const { storeInfo } = useStoreInfo();
  
  // State declarations
  const [templateHtml, setTemplateHtml] = useState("");
  const [deliveryNoteProducts, setDeliveryNoteProducts] = useState([]);
  const blocker = useUnsavedChanges(deliveryNoteProducts.length > 0);
  const [deliveryNoteNumber, setDeliveryNoteNumber] = useState("");
  const [deliveryNoteDate, setDeliveryNoteDate] = useState(new Date());
  const [deliveryNoteTime, setDeliveryNoteTime] = useState('');
  const [deliveryDateTime, setDeliveryDateTime] = useState('');
  const [previewHtml, setPreviewHtml] = useState("");
  const [openPreview, setOpenPreview] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [deliveryNoteVAT, setDeliveryNoteVAT] = useState(21);
  const [creatingDeliveryNote, setCreatingDeliveryNote] = useState(false);
  
  // Customer dialog state
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);

  // Product dialog state
  const [openProductDialog, setOpenProductDialog] = useState(false);

  // VAT rates from DB
  const [vatRates, setVatRates] = useState([]);

  // Note templates state
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const { t, i18n } = useTranslation();
  const language = useSelector((state) => state.settings?.language) || i18n.language || "nl";
  
  const debounceRef = useRef();

// Fetch VAT rates from database
  useEffect(() => {
    const fetchVatRates = async () => {
      try {
        const vatData = await vatService.getVatRates();
        const rates = vatData?.payload || vatData || [];
        setVatRates(rates);
      } catch (error) {
      }
    };
    fetchVatRates();
  }, []);

  // Fetch note templates for DeliveryNote
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        // Skip if templates already loaded
        if (noteTemplates.length > 0) return;
        
        const templatesData = await documentNoteTemplateService.getTemplatesByType('DeliveryNote');
        const allTemplates = templatesData?.payload || [];
        // Filter templates by DocumentType.DeliveryNote (3)
        const templates = allTemplates.filter(t => 
          t.DocumentType === 3 || // DocumentType.DeliveryNote
          t.DocumentType === 99   // DocumentType.All
        );
        setNoteTemplates(templates);
        
        // Select first template by default (only for new delivery notes, not edit mode)
        if (templates.length > 0 && !isEditMode && !customText) {
          const defaultTemplate = templates.find(t => t.IsDefault) || templates[0];
          setSelectedTemplate(defaultTemplate.Id);
          const plainText = stripHtml(defaultTemplate.NoteContent);
          if (plainText.length <= MAX_CUSTOM_TEXT_LENGTH) {
            dispatch(setCustomText(defaultTemplate.NoteContent));
          } else {
            const allowedText = plainText.slice(0, MAX_CUSTOM_TEXT_LENGTH);
            dispatch(setCustomText(allowedText));
          }
        }
      } catch (error) {
      }
    };
    fetchTemplates();
  }, []);

// Fetch initial data
  useEffect(() => {
    if (isEditMode) {
      return;
    }
    
    const fetchInitialData = async () => {
      setDataLoading(true);
      
      try {
        // Generate delivery note number locally (DDMMYY + 4 random digits)
        setDeliveryNoteNumber(generateDeliveryNoteNumber());

        // Fetch template
        let templateFile = "deliverynote-template-nl.html";
        if (language === "fr") templateFile = "deliverynote-template-fr.html";
        else if (language === "en") templateFile = "deliverynote-template-en.html";
        
        try {
          const response = await fetch(`/deliverynotetemplates/${templateFile}`);
          if (response.ok) {
            const html = await response.text();
            setTemplateHtml(html);
          } else {
            throw new Error(`Failed to load template: ${response.status}`);
          }
        } catch (templateError) {
          setTemplateHtml("");
          toast.warning(t("failed_to_load_template"));
        }
        
      } catch (error) {
        toast.error(t("failed_to_load_initial_data"));
      } finally {
        setDataLoading(false);
      }
    };

    fetchInitialData();
  }, [language, t, dispatch, isEditMode]);

  // Load delivery note for edit
  useEffect(() => {
    if (isEditMode && deliveryNoteId) {
      loadDeliveryNoteForEdit(deliveryNoteId);
    } else {
      dispatch(setSelectedCustomer(null));
      dispatch(setSelectedProduct(null));
      dispatch(setQuantity(1));
      dispatch(setPrice(""));
      dispatch(setCustomText(""));
      setDeliveryNoteProducts([]);
    }
  }, [deliveryNoteId, isEditMode]);

  // Pre-fill from sale order if navigated from sale order detail
  useEffect(() => {
    if (isEditMode || !location.state?.fromSaleOrder) return;

    const { customer, lineItems, vatPercentage, saleOrderNumber } = location.state;

    if (customer) {
      dispatch(setSelectedCustomer(customer));
    }

    if (lineItems && lineItems.length > 0) {
      setDeliveryNoteProducts(lineItems.map((item, i) => ({
        ...item,
        _rowKey: item._rowKey || `so_pre_${Date.now()}_${i}`,
      })));
    }

    if (vatPercentage) {
      setDeliveryNoteVAT(vatPercentage);
    }

    if (saleOrderNumber) {
      dispatch(setCustomText(`Ref: Sale Order ${saleOrderNumber}`));
    }

  }, []);

  // Load delivery note data for editing
  const loadDeliveryNoteForEdit = async (id) => {
    try {
      setDataLoading(true);
      const deliveryNoteData = await deliveryNoteService.getDeliveryNoteById(id);
      
      if (deliveryNoteData) {
        setDeliveryNoteNumber(deliveryNoteData.DeliveryNoteNumber || '');
        
        if (deliveryNoteData.DeliveryNoteDate || deliveryNoteData.CreatedDate) {
          setDeliveryNoteDate(new Date(deliveryNoteData.DeliveryNoteDate || deliveryNoteData.CreatedDate));
        }
        
        // Set delivery time if available
        if (deliveryNoteData.DeliveryTime) {
          setDeliveryNoteTime(deliveryNoteData.DeliveryTime);
        }
        
        // Combine date and time for datetime-local input
        if (deliveryNoteData.DeliveryNoteDate) {
          const dateStr = new Date(deliveryNoteData.DeliveryNoteDate).toISOString().split('T')[0];
          const timeStr = deliveryNoteData.DeliveryTime || '00:00';
          setDeliveryDateTime(`${dateStr}T${timeStr}`);
        }
        
        setDeliveryNoteVAT(Number(deliveryNoteData.VATPercentage || 21));
        dispatch(setCustomText(deliveryNoteData.CustomText || ''));
        
        // Set customer
        if (deliveryNoteData.Customer) {
          const customerData = {
            Id: deliveryNoteData.Customer.Id || deliveryNoteData.CustomerId,
            Name: deliveryNoteData.Customer.Name || deliveryNoteData.CustomerName,
            CompanyName: deliveryNoteData.Customer.CompanyName,
            Email: deliveryNoteData.Customer.Email,
            PhoneNumber: deliveryNoteData.Customer.PhoneNumber,
            VATNumber: deliveryNoteData.Customer.VATNumber,
            Street: deliveryNoteData.Customer.Street,
            City: deliveryNoteData.Customer.City,
            PostCode: deliveryNoteData.Customer.PostCode,
            Country: deliveryNoteData.Customer.Country,
            label: deliveryNoteData.Customer.CompanyName || deliveryNoteData.Customer.Name || deliveryNoteData.CustomerName,
            value: deliveryNoteData.Customer.Id || deliveryNoteData.CustomerId
          };
          dispatch(setSelectedCustomer(customerData));
        }
        
        // Set line items
        if (deliveryNoteData.LineItems && deliveryNoteData.LineItems.length > 0) {
          const mappedProducts = deliveryNoteData.LineItems.map((item, index) => {
            const totalExcl = Number(item.TotalExcl || 0);
            const vatPercentage = item.VAT || deliveryNoteData.VATPercentage || 21;
            
            let totalIncl = item.TotalIncl ? Number(item.TotalIncl) : totalExcl * (1 + vatPercentage / 100);
            const vatAmount = item.VATAmount ? Number(item.VATAmount) : totalIncl - totalExcl;
            
            return {
              Id: item.ProductId,
              Name: item.DisplayName || item.ProductName || item.Name || 'Unknown Product',
              Quantity: Number(item.Quantity || 1),
              Price: String(item.UnitPrice || item.Price || 0),
              VAT: vatPercentage,
              TotalExcl: totalExcl.toFixed(2),
              TotalIncl: totalIncl.toFixed(2),
              VATAmount: vatAmount.toFixed(2),
              TotalVAT: vatAmount.toFixed(2),
              TotalInclVAT: totalIncl.toFixed(2),
              _rowKey: Date.now() + index
            };
          });
          setDeliveryNoteProducts(mappedProducts);
        }
        
        // Fetch template for edit mode
        let templateFile = "deliverynote-nl.html";
        if (language === "fr") templateFile = "deliverynote-fr.html";
        else if (language === "en") templateFile = "deliverynote-en.html";
        
        try {
          const response = await fetch(`/deliverynotetemplates/${templateFile}`);
          if (response.ok) {
            const html = await response.text();
            setTemplateHtml(html);
          } else {
            setTemplateHtml("");
          }
        } catch (templateError) {
          setTemplateHtml("");
        }
        
        toast.success(t('delivery_note_loaded_for_editing') || 'Delivery note loaded for editing');
      }
    } catch (error) {
      toast.error(t('failed_to_load_delivery_note') || 'Failed to load delivery note');
      navigate('/delivery-notes');
    } finally {
      setDataLoading(false);
    }
  };

  // Add product to delivery note
  const handleAddProduct = (productToAdd = null, quantityToAdd = null, priceToAdd = null) => {
    const product = productToAdd || selectedProduct;
    const qty = quantityToAdd || quantity;
    const productPrice = priceToAdd || price;
    
    if (product && qty > 0) {
      const netPrice = Math.round((parseFloat(productPrice) || 0) * 100) / 100;
      const totalExclForProduct = Math.round(netPrice * qty * 100) / 100;
      const vatAmountForProduct = Math.round(totalExclForProduct * (deliveryNoteVAT / 100) * 100) / 100;
      const totalIncl = totalExclForProduct + vatAmountForProduct;

      const existingProductIndex = deliveryNoteProducts.findIndex(
        (p) => p.Id === product.Id && parseFloat(p.Price) === netPrice
      );

      if (existingProductIndex !== -1) {
        const updatedProducts = [...deliveryNoteProducts];
        const existingProduct = updatedProducts[existingProductIndex];
        const newQuantity = existingProduct.Quantity + qty;
        const newTotalExcl = Math.round(netPrice * newQuantity * 100) / 100;
        const newVATAmount = Math.round(newTotalExcl * (deliveryNoteVAT / 100) * 100) / 100;
        const newTotalIncl = newTotalExcl + newVATAmount;
        updatedProducts[existingProductIndex] = {
          ...existingProduct,
          Quantity: newQuantity,
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2),
          TotalIncl: newTotalIncl.toFixed(2),
          TotalVAT: newVATAmount.toFixed(2),
          TotalInclVAT: newTotalIncl.toFixed(2),
        };
        setDeliveryNoteProducts(updatedProducts);
      } else {
        setDeliveryNoteProducts([...deliveryNoteProducts, {
          ...product,
          Quantity: qty,
          Price: netPrice.toFixed(2),
          TotalExcl: totalExclForProduct.toFixed(2),
          VATAmount: vatAmountForProduct.toFixed(2),
          TotalIncl: totalIncl.toFixed(2),
          TotalVAT: vatAmountForProduct.toFixed(2),
          TotalInclVAT: totalIncl.toFixed(2),
          _rowKey: Date.now() + Math.random(),
        }]);
      }
      
      if (!productToAdd) {
        dispatch(setSelectedProduct(null));
        dispatch(setQuantity(1));
        dispatch(setPrice(""));
      }
    }
  };

  const handleRemoveProduct = (rowKey) => {
    setDeliveryNoteProducts(deliveryNoteProducts.filter((p) => p._rowKey !== rowKey));
  };

  // Calculate totals (no discount for delivery notes)
  const totalExcl = deliveryNoteProducts.reduce((sum, p) => sum + Number(p.TotalExcl), 0);
  const totalGross = deliveryNoteProducts.reduce((sum, p) => sum + Number(p.TotalIncl), 0);

  const grandTotal = totalGross;
  const vatRate = deliveryNoteVAT / 100;
  const totalExclAfterDiscount = grandTotal / (1 + vatRate);
  const totalVAT = grandTotal - totalExclAfterDiscount;

  // Format date
  function formatDate(date) {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Get filled template for preview
  const getFilledTemplate = () => {
    if (!templateHtml) {
      return "";
    }
    
    const totalQuantity = deliveryNoteProducts.reduce((sum, p) => sum + Number(p.Quantity), 0);
    const cleanCustomText = stripHtml(customText).trim();

    const customTextSection = cleanCustomText
      ? `<div style="margin: 24px 0 16px 0; padding: 12px 16px; background: #fffbe6; border: 1px solid #ffe082; border-radius: 6px;">
        <span style="font-weight: bold; color: #d32f2f; font-size: 1.1em; margin-right: 8px;">${t("notes")}</span>
        <span style="font-weight: 600; color: #795548;">${cleanCustomText}</span>
      </div>`
      : "";
    
    // Notes section - only show if there's content
    const notesSection = cleanCustomText
      ? `<div class="notes-section">
        <h4>Notes</h4>
        <p>${cleanCustomText}</p>
      </div>`
      : "";

    const VATSection = deliveryNoteVAT > 0
      ? `<div><span>${deliveryNoteVAT}% ${t("vat")} on €${totalExcl.toFixed(2)}:</span><span>€${totalVAT.toFixed(2)}</span></div>`
      : `<div><span>0% ${t("vat")}:</span><span>€0.00</span></div>`;

    // No discount for delivery notes
    const DiscountRow = '';

    const ProductRows = deliveryNoteProducts.map((p, i) => `
      <tr${i % 2 === 1 ? ' class="alternate-row"' : ''}>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt;">${i + 1}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt;">${p.Name}</td>
        <td style="padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 8.5pt; text-align: center;">${p.Quantity}</td>
      </tr>
    `).join('');

    let filledTemplate = templateHtml
      // Company Info
      .replace(/{{CompanyName}}/g, storeInfo?.name || "N/A")
      .replace(/{{CompanyVAT}}/g, storeInfo?.vat || "N/A")
      .replace(/{{CompanyEmail}}/g, storeInfo?.email || "N/A")
      .replace(/{{CompanyPhone}}/g, storeInfo?.phone || "N/A")
      .replace(/{{CompanyAddress}}/g, storeInfo?.address || "")
      .replace(/{{CompanyCity}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || "")
      .replace(/{{CompanyStreetAddress}}/g, storeInfo?.address || "")
      .replace(/{{CompanyCityCountry}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(' ') || "")
      .replace(/{{CompanyIBAN}}/g, storeInfo?.iban || "N/A")
      .replace(/{{CompanyBankDetail}}/g, storeInfo?.iban || "N/A")
      .replace(/{{CompanyBankAccount}}/g, storeInfo?.iban || "N/A")
      
      // Customer Info
      .replace(/{{CustomerName}}/g, selectedCustomer?.CompanyName || selectedCustomer?.Name || "N/A")
      .replace(/{{CustomerId}}/g, selectedCustomer?.Id || "")
      .replace(/{{CustomerVAT}}/g, selectedCustomer?.VATNumber || selectedCustomer?.vat || "N/A")
      .replace(/{{CustomerNumber}}/g, selectedCustomer?.Id || "")
      .replace(/{{CustomerEmail}}/g, selectedCustomer?.Email || "N/A")
      .replace(/{{CustomerPhone}}/g, selectedCustomer?.PhoneNumber || "N/A")
      .replace(/{{CustomerAddress}}/g, selectedCustomer?.Street ? `${selectedCustomer?.Street || ""} ${selectedCustomer?.Box || ""}`.trim() : "N/A")
      .replace(/{{CustomerCity}}/g, [selectedCustomer?.PostCode, selectedCustomer?.City, selectedCustomer?.Country].filter(Boolean).join(' ') || "N/A")
      .replace(/{{CustomerStreetAddress}}/g, `${selectedCustomer?.Street || ""} ${selectedCustomer?.Box || ""}`.trim() || "N/A")
      .replace(/{{CustomerCityCountry}}/g, [selectedCustomer?.PostCode, selectedCustomer?.City, selectedCustomer?.Country].filter(Boolean).join(' ') || "N/A")
      
      // Delivery Note Info
      .replace(/{{DeliveryNoteNumber}}/g, deliveryNoteNumber)
      .replace(/{{DeliveryNoteDate}}/g, deliveryNoteTime ? `${formatDate(deliveryNoteDate)} ${deliveryNoteTime}` : formatDate(deliveryNoteDate))
      .replace(/{{DeliveryTime}}/g, deliveryNoteTime || '')
      .replace(/{{Status}}/g, "")
      .replace(/status-{{Status}}/g, "")
      
      // Product Info (for backwards compatibility)
      .replace(/{{ProductName}}/g, selectedProduct?.Name || "")
      .replace(/{{ProductId}}/g, selectedProduct?.Id || "")
      .replace(/{{Quantity}}/g, quantity.toString())
      .replace(/{{Price}}/g, price.toString())
      .replace(/{{VAT}}/g, deliveryNoteVAT.toString())
      
      // Product Rows and Line Items
      .replace(/{{LineItems}}/g, ProductRows)
      .replace(/{{{ProductRows}}}/g, ProductRows)
      .replace(/{{TotalQuantity}}/g, totalQuantity.toString())
      .replace(/{{{DiscountRow}}}/g, DiscountRow)
      
      // Financial Totals
      .replace(/{{Subtotal}}/g, `€${totalGross.toFixed(2)}`)
      .replace(/{{TotalExcl}}/g, totalExcl.toFixed(2))
      .replace(/{{VATPercentage}}/g, deliveryNoteVAT.toString())
      .replace(/{{TotalVAT}}/g, totalVAT.toFixed(2))
      .replace(/{{VATAmount}}/g, `€${totalVAT.toFixed(2)}`)
      .replace(/{{Discount}}/g, "€0.00")
      .replace(/{{TotalAmount}}/g, grandTotal.toFixed(2))
      .replace(/{{Total}}/g, `€${grandTotal.toFixed(2)}`)
      .replace(/{{GrandTotal}}/g, grandTotal.toFixed(2))
      
      // Payment placeholders (kept for template backwards compatibility)
      .replace(/{{InvoicePaidAmount}}/g, '0.00')
      .replace(/{{InvoiceDueAmount}}/g, '0.00')
      .replace(/{{{PaymentMethodsSection}}}/g, '')
      
      // Page Numbering
      .replace(/{{PageNumber}}/g, '1')
      .replace(/{{TotalPages}}/g, '1')
      
      // VAT Section
      .replace(/{{VATSection}}/g, VATSection)
      
      // Custom Text and Notes
      .replace(/{{CustomText}}/g, cleanCustomText)
      .replace(/{{CustomTextSection}}/g, customTextSection)
      .replace(/<div class="notes-section">\s*<h4>Notes<\/h4>\s*<p>{{Notes}}<\/p>\s*<\/div>/g, notesSection)
      .replace(/{{Notes}}/g, cleanCustomText);

    return filledTemplate;
  };

  // Preview delivery note
  const handlePreview = () => {
    if (!selectedCustomer) {
      toast.error(t("please_select_customer") || "Please select a customer");
      return;
    }
    
    if (deliveryNoteProducts.length === 0) {
      toast.error(t("please_add_products") || "Please add at least one product");
      return;
    }
    
    if (!templateHtml) {
      toast.error(t("failed_to_load_template") || "Template not loaded. Please refresh the page.");
      return;
    }
    
    try {
      const filled = getFilledTemplate();
      if (!filled) {
        toast.error("Failed to generate preview. Please try again.");
        return;
      }
      setPreviewHtml(filled);
      setOpenPreview(true);
    } catch (error) {
      toast.error("Failed to generate preview. Please try again.");
    }
  };

  // Generate PDF
  const handleGeneratePDF = async () => {
    try {
      if (!storeInfo) {
        toast.warning('Loading store information...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (!storeInfo) {
          toast.error('Store information not available. Please refresh the page.');
          return;
        }
      }

      const rawHtml = getFilledTemplate();
      // Inject a style to force-disable box-shadow and ensure full-width rendering for PDF
      const filledHtml = rawHtml.replace(
        '</style>',
        `  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    .delivery-note-container { box-shadow: none !important; margin: 0 !important; width: 794px !important; max-width: 794px !important; }
  </style>`
      );
      const opt = {
        margin: 0,
        filename: `delivery-note-${deliveryNoteNumber}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { 
          scale: 2, 
          useCORS: true, 
          logging: false,
          windowWidth: 794,
          letterRendering: true
        },
        jsPDF: { 
          unit: "mm", 
          format: "a4", 
          orientation: "portrait" 
        },
        pagebreak: { 
          mode: ['avoid-all', 'css', 'legacy'],
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: ['tr', 'td', 'img', '.totals-section', '.notes-section']
        }
      };

      await html2pdf().set(opt).from(filledHtml).save();
      
    } catch (error) {
      toast.error(t("failed_to_generate_pdf") || "Failed to generate PDF");
    }
  };

  // Save delivery note
  const handleSaveDeliveryNote = async () => {
    // Validation
    if (!selectedCustomer) {
      toast.error(t("please_select_customer") || "Please select a customer");
      return;
    }
    
    if (deliveryNoteProducts.length === 0) {
      toast.error(t("please_add_products") || "Please add at least one product");
      return;
    }

    setCreatingDeliveryNote(true);

    try {
      const deliveryNoteData = {
        ...(isEditMode && deliveryNoteId ? { Id: parseInt(deliveryNoteId) } : {}),
        DeliveryNoteNumber: deliveryNoteNumber,
        DeliveryNoteDate: deliveryNoteDate.toISOString(),
        DeliveryTime: deliveryNoteTime || null,
        CustomerId: selectedCustomer?.Id || selectedCustomer?.value,
        CustomText: stripHtml(customText),
        VATPercentage: deliveryNoteVAT,
        TotalAmount: grandTotal,
        SubTotal: parseFloat(totalExclAfterDiscount.toFixed(2)),
        TotalVATAmount: parseFloat(totalVAT.toFixed(2)),
        Discount: 0,
        DiscountType: 'none',
        DiscountValue: 0,
        LineItems: deliveryNoteProducts.map((p) => ({
          ProductId: p.Id,
          Quantity: p.Quantity,
          Price: p.Price,
          TotalExcl: p.TotalExcl,
          VatAmount: p.VATAmount,
          TotalIncl: p.TotalIncl,
          TotalVAT: p.TotalVAT || p.VATAmount,
          TotalInclVAT: p.TotalInclVAT || p.TotalIncl,
        })),
      };

      let response;
      if (isEditMode && deliveryNoteId) {
        response = await deliveryNoteService.updateDeliveryNote(deliveryNoteId, deliveryNoteData);
      } else {
        response = await deliveryNoteService.addDeliveryNote(deliveryNoteData);
      }

      if (response) {
        toast.success(isEditMode ? t("delivery_note_updated_success") || "Delivery note updated successfully" : t("delivery_note_created_success") || "Delivery note created successfully");
        
        if (!isEditMode) {
          resetDeliveryNoteForm();
        }
        
        setTimeout(() => {
          navigate('/sales/delivery-notes');
        }, 1500);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      
      if (error.response?.status === 400) {
        toast.error(t("invalid_delivery_note_data") || "Invalid delivery note data");
      } else if (error.response?.status === 401) {
        toast.error(t("session_expired") || "Session expired");
      } else if (error.code === 'ERR_NETWORK') {
        toast.error(t("network_error") || "Network error");
      } else {
        toast.error(error.response?.data?.message || (isEditMode ? t("delivery_note_updated_error") : t("delivery_note_created_error")) || "An error occurred");
      }
    } finally {
      setCreatingDeliveryNote(false);
    }
  };

  // Reset form
  const resetDeliveryNoteForm = async () => {
    dispatch(setSelectedCustomer(null));
    dispatch(setSelectedProduct(null));
    dispatch(setQuantity(1));
    dispatch(setPrice(""));
    dispatch(setCustomText(""));
    setDeliveryNoteProducts([]);
    setDeliveryNoteVAT(21);
    setDeliveryNoteDate(new Date());
    setDeliveryNoteTime('');
    setDeliveryDateTime('');
    setDeliveryNoteNumber(generateDeliveryNoteNumber());
  };

  // Load customer options with debounce
  const customerDebounceRef = useRef();
  const loadCustomerOptions = (inputValue, callback) => {
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);

    customerDebounceRef.current = setTimeout(async () => {
      if (!inputValue || inputValue.trim().length < 2) {
        return callback([]);
      }

      try {
        const customers = await searchCustomers(inputValue);
        const customersArray = Array.isArray(customers) ? customers : [];
        callback(customersArray);
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error(t("session_expired"));
        } else if (error.code === 'ERR_NETWORK') {
          toast.error(t("network_error"));
        }
        callback([]);
      }
    }, 400);
  };

  // Load product options
  const loadProductOptions = (inputValue, callback) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!inputValue) return callback([]);

      try {
        const barcodeLengths = [8, 12, 13, 14];
        let paramsObj;

        if (barcodeLengths.includes(inputValue.length) && /^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (/^\d+$/.test(inputValue)) {
          paramsObj = { skuBarCode: inputValue };
        } else if (inputValue.length >= 3) {
          paramsObj = { name: inputValue };
        } else {
          return callback([]);
        }

        const data = await searchProducts(paramsObj);

        const productsArray = Array.isArray(data?.payload)
          ? data.payload
          : Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];

        const limitedProducts = productsArray.slice(0, 50);

        callback(
          limitedProducts.map((p) => ({
            value: p.Id,
            label: `${p.Name} (€${p.SalePrice || 0})${p.SKU ? " - SKU: " + p.SKU : ""}${p.BarCode ? " - BarCode: " + p.BarCode : ""}`,
            ...p,
          }))
        );
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error(t("session_expired"));
        } else if (error.code === 'ERR_NETWORK') {
          toast.error(t("network_error"));
        }
        
        callback([]);
      }
    }, 350);
  };

  // Recalculate when VAT changes
  useEffect(() => {
    if (deliveryNoteProducts.length > 0) {
      const updatedProducts = deliveryNoteProducts.map(product => {
        const totalIncl = Number(product.TotalIncl);
        const newTotalExcl = totalIncl / (1 + deliveryNoteVAT / 100);
        const newVATAmount = totalIncl - newTotalExcl;
        const newPrice = newTotalExcl / Number(product.Quantity);
        
        return {
          ...product,
          Price: newPrice.toFixed(2),
          VAT: deliveryNoteVAT,
          TotalExcl: newTotalExcl.toFixed(2),
          VATAmount: newVATAmount.toFixed(2)
        };
      });
      setDeliveryNoteProducts(updatedProducts);
    }
  }, [deliveryNoteVAT]);

  // Customer dialog handlers
  const handleOpenCustomerDialog = () => {
    setOpenCustomerDialog(true);
  };

  const handleCloseCustomerDialog = () => {
    setOpenCustomerDialog(false);
  };

  const handleCustomerCreated = (createdCustomer) => {
    dispatch(setSelectedCustomer(createdCustomer));
    toast.success(t("customer_created_and_selected") || "Customer created and selected!");
    setOpenCustomerDialog(false);
  };

// Handler for template selection
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = noteTemplates.find(t => t.Id === templateId);
    if (template) {
      const plainText = stripHtml(template.NoteContent);
      if (plainText.length <= MAX_CUSTOM_TEXT_LENGTH) {
        dispatch(setCustomText(template.NoteContent));
      } else {
        const allowedText = plainText.slice(0, MAX_CUSTOM_TEXT_LENGTH);
        dispatch(setCustomText(allowedText));
        toast.warning(t("template_content_truncated"));
      }
    }
  };

  // Show loading or error state
  if (!templateHtml && !dataLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper 
          elevation={3}
          sx={{ 
            p: 4,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.08),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <LocalShippingIcon sx={{ fontSize: 60, color: 'error.main' }} />
            <Typography variant="h6" color="error" textAlign="center">
              {t("failed_to_load_template") || "Failed to load template"}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {t("please_refresh_or_contact_support") || "Please refresh or contact support"}
            </Typography>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              startIcon={<RefreshIcon />}
            >
              {t("refresh_page") || "Refresh Page"}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{
      background: '#f5f5f5',
      minHeight: '100vh',
      py: 3
    }}>
      <Container 
        maxWidth={false}
        sx={{ 
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1600px',
          margin: '0 auto'
        }}
      >
        {/* Back Button */}
        {isEditMode && deliveryNoteId && (
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/delivery-notes/view/${deliveryNoteId}`)}
            sx={{
              mb: 2,
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: '#667eea',
              color: '#667eea',
              '&:hover': {
                borderColor: '#764ba2',
                bgcolor: alpha('#667eea', 0.05)
              }
            }}
          >
            {t("back_to_view") || "Back to View"}
          </Button>
        )}

        {/* Delivery Note Container - Matches Template A4 Style */}
        <Paper
          elevation={3}
          sx={{
            background: 'white',
            boxShadow: '0 0 20px rgba(0,0,0,0.1)',
            borderRadius: '8px',
            overflow: 'visible',
            maxWidth: '100%'
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 }, pt: { xs: 5, sm: 5, md: 6, lg: 7 } }}>
            {/* TOP SECTION: Company Info (Left) + Customer (Right) - Same as Sale Order */}
            <DocumentHeaderSection
              storeInfo={storeInfo}
              selectedCustomer={selectedCustomer}
              onCustomerChange={(c) => dispatch(setSelectedCustomer(c))}
              onCustomerClear={() => dispatch(setSelectedCustomer(null))}
              onAddCustomerClick={handleOpenCustomerDialog}
              onEditCustomerClick={() => dispatch(setSelectedCustomer(null))}
              loadCustomerOptions={loadCustomerOptions}
              disabled={false}
              documentLabel={t('delivery_note') || 'Delivery Note'}
              accentColor="#667eea"
            />

            {/* DELIVERY NOTE DETAILS ROW - with DateTime picker */}
            <DocumentDetailsBar
              documentDate={deliveryDateTime}
              onDateChange={(datetime) => {
                setDeliveryDateTime(datetime);
                // Extract date and time from datetime-local value
                if (datetime) {
                  const [dateStr, timeStr] = datetime.split('T');
                  setDeliveryNoteDate(new Date(dateStr));
                  setDeliveryNoteTime(timeStr || '');
                } else {
                  setDeliveryNoteDate(null);
                  setDeliveryNoteTime('');
                }
              }}
              vatPercentage={deliveryNoteVAT}
              onVatChange={setDeliveryNoteVAT}
              vatRates={vatRates}
              itemsCount={deliveryNoteProducts.length}
              isTvaIncluded={false}
              onTvaToggle={() => {}}
              dateLabel={t("expected_delivery") || "Expected Delivery"}
              showPriceTypeToggle={false}
              documentNumber={deliveryNoteNumber}
              documentNumberLabel={t("delivery_note") || "Delivery Note"}
              useDateTime={true}
            />

            {/* PRODUCTS SECTION: Table with Header Always Visible */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, width: '50px' }}>
                        {t("no") || "NO"}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5 }}>
                        {t("product_name") || "PRODUCT NAME"}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'center', width: '120px' }}>
                        {t("quantity") || "QUANTITY"}
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.5px', py: 1.5, textAlign: 'center', width: '80px' }}>
                        {t("actions") || "ACTIONS"}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Added Products Rows */}
                    {deliveryNoteProducts.map((p, rowIndex) => (
                        <TableRow 
                          key={p._rowKey}
                          sx={{
                            '&:hover': { bgcolor: '#f8f9fa' },
                            borderBottom: '1px solid #e0e0e0'
                          }}
                        >
                          <TableCell sx={{ py: 1.5, fontSize: '0.85rem', color: '#888', width: '50px' }}>
                            {rowIndex + 1}
                          </TableCell>
                          <TableCell sx={{ py: 1.5, fontSize: '0.85rem', color: '#333' }}>
                            <Typography variant="body2" fontWeight={500}>
                              {p.Name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                            <TextField
                              type="number"
                              value={p.Quantity}
                              onChange={(e) => {
                                const newQuantity = parseFloat(e.target.value) || 0;
                                if (newQuantity <= 0) return;
                                setDeliveryNoteProducts(deliveryNoteProducts.map((prod) => {
                                  if (prod._rowKey === p._rowKey) {
                                    const pricePerUnit = parseFloat(prod.Price);
                                    const newTotalExcl = pricePerUnit * newQuantity;
                                    const newVATAmount = newTotalExcl * (deliveryNoteVAT / 100);
                                    const newTotalIncl = newTotalExcl + newVATAmount;
                                    return {
                                      ...prod,
                                      Quantity: newQuantity,
                                      TotalExcl: newTotalExcl.toFixed(2),
                                      VATAmount: newVATAmount.toFixed(2),
                                      TotalIncl: newTotalIncl.toFixed(2),
                                      TotalVAT: newVATAmount.toFixed(2),
                                      TotalInclVAT: newTotalIncl.toFixed(2),
                                    };
                                  }
                                  return prod;
                                }));
                              }}
                              size="small"
                              inputProps={{ min: 1, step: 1 }}
                              sx={{
                                width: '80px',
                                '& .MuiOutlinedInput-root': {
                                  '& input': { textAlign: 'center', py: 0.5 }
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 1.5, textAlign: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveProduct(p._rowKey)}
                              sx={{
                                color: '#d32f2f',
                                '&:hover': { bgcolor: alpha('#d32f2f', 0.1) }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    
                    {/* Product Search Row - For adding new products */}
                    <TableRow sx={{ bgcolor: '#fffef0', borderTop: '2px solid #667eea' }}>
                      <TableCell colSpan={4} sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => setOpenProductDialog(true)}
                            sx={{
                              color: '#667eea',
                              bgcolor: alpha('#667eea', 0.1),
                              '&:hover': { bgcolor: alpha('#667eea', 0.2) }
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <Box sx={{ flex: 1 }}>
                            <ProductSelect
                              value={null}
                              onChange={(selectedOption) => {
                                if (selectedOption) {
                                  const productPrice = selectedOption.SalePrice || selectedOption.Price || selectedOption.price || "";
                                  
                                  // Immediately add the product with the selected data
                                  handleAddProduct(selectedOption, 1, productPrice);
                                }
                              }}
                              loadOptions={loadProductOptions}
                              placeholder={t("search_products") || "Search and add products..."}
                              menuPortalTarget={document.body}
                            />
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Totals Row - At the bottom */}
                    {deliveryNoteProducts.length > 0 && (
                      <TableRow sx={{ bgcolor: '#f0f0f0', borderTop: '2px solid #667eea' }}>
                        <TableCell colSpan={2} sx={{ py: 1.5, fontWeight: 700, fontSize: '0.9rem', color: '#2c3e50' }}>
                          {t("total") || "TOTAL"}
                        </TableCell>
                        <TableCell sx={{ py: 1.5, textAlign: 'center', fontWeight: 700, fontSize: '0.9rem', color: '#667eea' }}>
                          {deliveryNoteProducts.reduce((sum, p) => sum + parseFloat(p.Quantity || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}></TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>

            {/* BOTTOM SECTION: Notes & Actions */}
            <Grid container spacing={3}>
              {/* LEFT: Custom Text/Notes */}
              <Grid item xs={12} md={7}>
                <Box sx={{
                  p: 3,
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e0e0e0',
                  borderLeft: '4px solid #667eea',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                    <NotesIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                    {t("notes")}
                  </Typography>
                  
                  {/* Template Selector */}
                  {noteTemplates.length > 0 && (
                    <FormControl fullWidth sx={{ mb: 2, bgcolor: 'white' }}>
                      <InputLabel>{t("select_template")}</InputLabel>
                      <Select
                        value={selectedTemplate}
                        onChange={(e) => handleTemplateSelect(e.target.value)}
                        label={t("select_template")}
                      >
                        <MenuItem value="">
                          <em>{t("none")}</em>
                        </MenuItem>
                        {noteTemplates.map((template) => (
                          <MenuItem key={template.Id} value={template.Id}>
                            {template.TemplateName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  
                  <TextField
                    multiline
                    rows={4}
                    value={customText}
                    onChange={(e) => {
                      const newText = e.target.value;
                      if (stripHtml(newText).length <= MAX_CUSTOM_TEXT_LENGTH) {
                        dispatch(setCustomText(newText));
                      }
                    }}
                    placeholder={t("add_custom_notes") || "Add custom notes..."}
                    fullWidth
                    sx={{ bgcolor: 'white', borderRadius: '4px' }}
                  />
                  <Typography
                    variant="caption"
                    color={stripHtml(customText).length > MAX_CUSTOM_TEXT_LENGTH ? 'error' : 'text.secondary'}
                    sx={{ mt: 1, display: 'block' }}
                  >
                    {stripHtml(customText).length} / {MAX_CUSTOM_TEXT_LENGTH}
                  </Typography>
                </Box>
              </Grid>

              {/* RIGHT: Summary & Action Buttons */}
              <Grid item xs={12} md={5}>
                <Stack spacing={3}>
                  {deliveryNoteProducts.length === 0 && (
                    <Box sx={{
                      p: 2,
                      borderRadius: '10px',
                      bgcolor: alpha(theme.palette.error.main, 0.08),
                      border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="body1" color="error" textAlign="center" fontWeight="medium">
                        {t("please_add_at_least_one_product")}
                      </Typography>
                    </Box>
                  )}

                  {/* Summary Box */}
                  <Box sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                    width: '100%'
                  }}>
                    <Box sx={{ p: 2, background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                        {t("summary")}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="#555">{t("total_products") || "Total Products"}</Typography>
                          <Typography variant="body1" fontWeight={600}>{deliveryNoteProducts.length}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#f0f4ff', borderRadius: '4px' }}>
                          <Typography variant="body2" color="#667eea" fontWeight={600}>{t("total_quantity") || "Total Quantity"}</Typography>
                          <Typography variant="body1" fontWeight={700} color="#667eea">
                            {deliveryNoteProducts.reduce((sum, p) => sum + parseFloat(p.Quantity || 0), 0)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>

                  {/* Primary Action Buttons */}
                  <Stack spacing={2}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreview}
                      disabled={!selectedCustomer || deliveryNoteProducts.length === 0}
                      sx={{
                        py: 1.5,
                        borderRadius: '12px',
                        borderWidth: '2px',
                        borderColor: '#667eea',
                        color: '#667eea',
                        fontSize: '1rem',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        '&:hover': {
                          borderWidth: '2px',
                          borderColor: '#764ba2',
                          backgroundColor: alpha('#667eea', 0.05)
                        }
                      }}
                    >
                      {t("preview_delivery_note")}
                    </Button>

                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={creatingDeliveryNote ? null : <SaveIcon />}
                      onClick={handleSaveDeliveryNote}
                      disabled={!selectedCustomer || deliveryNoteProducts.length === 0 || creatingDeliveryNote}
                      sx={{
                        py: 1.5,
                        borderRadius: '12px',
                        background: "linear-gradient(45deg, #667eea 20%, #764ba2 90%)",
                        boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                        fontSize: '1rem',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        transition: 'all 0.3s',
                        '&:hover': {
                          transform: creatingDeliveryNote ? 'none' : 'translateY(-2px)',
                          boxShadow: '0 10px 25px rgba(102, 126, 234, 0.4)'
                        },
                        '&:disabled': {
                          background: 'linear-gradient(45deg, #9ca3af 20%, #6b7280 90%)',
                        }
                      }}
                    >
                      {creatingDeliveryNote ? (isEditMode ? t("updating_delivery_note") : t("creating_delivery_note")) : (isEditMode ? t("update_delivery_note") : t("create_delivery_note"))}
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Preview Dialog */}
        <InvoicePreviewDialog
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          html={previewHtml}
        />

        {/* Add Customer Dialog - Reusable Component */}
        <AddCustomerDialog
          open={openCustomerDialog}
          onClose={handleCloseCustomerDialog}
          onCustomerCreated={handleCustomerCreated}
          optionalFields={{ vat: true, email: true, phone: true }}
          hidePeppol={true}
        />

        {/* Add Product Dialog */}
        <AddProductDialog
          open={openProductDialog}
          onClose={() => setOpenProductDialog(false)}
          onProductCreated={(createdProduct) => {
            if (createdProduct && createdProduct.Id) {
              const productPrice = createdProduct.Price || createdProduct.SalePrice || createdProduct.price || 0;
              const productForSelection = {
                value: createdProduct.Id,
                label: `${createdProduct.Name} (\u20ac${productPrice})`,
                Id: createdProduct.Id,
                Name: createdProduct.Name,
                SKU: createdProduct.SKU,
                SalePrice: productPrice,
                Price: productPrice,
              };
              dispatch(setSelectedProduct(productForSelection));
              dispatch(setPrice(productPrice.toString()));
              toast.success(t("product_created_and_selected") || "Product created and selected!");
            }
            setOpenProductDialog(false);
          }}
        />
      </Container>

      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onDiscard={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </Box>
  );
}

export default AddDeliveryNotePage;
